import React, { createContext, useCallback, useContext, useState } from "react";

// Utils
import { fetchWithToken, fetchWithoutToken } from "../api/fetch";
import { ChatContext } from "./chat/ChatContext";
import { types } from "../types/types";

export const AuthContext = createContext();

// Estado inicial del contexto de autenticación
const initialState = {
  uid: null,
  checking: true,
  logged: false,
  name: null,
  user: null,
  email: null,
  photo: null,
  role: null,
  permissions: null,
  conected: null,
  color: "#82818d",
};

/**
 * Proveedor de autenticación
 *
 * @param {object} children - Componentes hijos
 * @returns {object}
 */
export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(initialState);
  const { dispatch } = useContext(ChatContext);

  /**
   * Función para el inicio de sesión
   *
   * @param {string} user - Usuario
   * @param {string} password - Contraseña
   * @returns {boolean} - Éxito de la operación
   */
  const login = async (user, password) => {
    const resp = await fetchWithoutToken(
      "agente/login",
      { user, password },
      "POST"
    );

    if (resp.ok) {
      localStorage.setItem("token", resp.token);

      const { agente } = resp;

      setAuth({
        uid: agente.id_agent,
        checking: false,
        logged: true,
        name: agente.name,
        user: agente.user,
        email: agente.email,
        role: agente.rol,
        photo: agente.foto,
        permissions: JSON.parse(agente.acceso),
        color: agente.color,
      });
    }

    return resp.ok;
  };

  /**
   * Verifica el token del usuario
   * Si el token es válido, se renueva
   *
   * @returns {boolean} - Éxito de la operación
   */
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      // Si no hay token, se marca como no autenticado
      setAuth({
        ...initialState,
        checking: false,
      });

      return false;
    }

    // Se verifica el token
    const resp = await fetchWithToken("agente/renew");

    if (!resp.ok) {
      // Si el token no es válido, se marca como no autenticado
      setAuth({
        ...initialState,
        checking: false,
      });

      return false;
    }

    // Se guarda el token en el local storage
    localStorage.setItem("token", resp.token);

    const { agente } = resp;

    // Se actualiza el estado de autenticación
    setAuth({
      uid: agente.id_agent,
      checking: false,
      logged: true,
      name: agente.name,
      user: agente.user,
      email: agente.email,
      role: agente.rol,
      photo: agente.foto,
      permissions: JSON.parse(agente.acceso),
      color: agente.color,
    });

    return true;
  }, []);

  /**
   * Permite cerrar sesión
   * Y elimina el token del usuario
   *
   * @returns {void}
   */
  const logout = () => {
    localStorage.removeItem("token");

    // Se cierra la sesión
    dispatch({
      type: types.cerrarSesion,
    });

    // Se reinicia el estado de autenticación
    setAuth({
      ...initialState,
      checking: false,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        verifyToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
